import React, { useState } from 'react'
import { analytics } from '../services/analytics'
import { db } from '../services/firebase'
import { collection, getDocs, doc, deleteDoc, query } from 'firebase/firestore'
import PasswordAuth from '../components/PasswordAuth'
import './DatabaseManagement.css'

interface SummaryData {
  metric: string
  total: number
  mobile: number
  desktop: number
  pc: number
  mobilePercent: string
  pcPercent: string
}

interface SurveyData {
  rating: string
  count: number
  percentage: string
}

interface DetailedEventLog {
  timestamp: string
  event: string
  device: string
  additionalInfo: string
}

const DatabaseManagement: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<string>('')
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info')

  const showMessage = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setMessage(msg)
    setMessageType(type)
    setTimeout(() => setMessage(''), 5000)
  }

  const getDeviceBreakdown = (events: any[], eventType: string) => {
    const filteredEvents = events.filter(e => e.eventType === eventType)
    const uniqueSessions = new Set()
    const deviceCounts = { mobile: 0, desktop: 0 }
    
    filteredEvents.forEach(event => {
      if (!uniqueSessions.has(event.sessionId)) {
        uniqueSessions.add(event.sessionId)
        const deviceType = event.deviceType
        if (deviceType === 'mobile' || deviceType === 'tablet') {
          deviceCounts.mobile++
        } else if (deviceType === 'desktop') {
          deviceCounts.desktop++
        }
      }
    })
    
    return deviceCounts
  }


  const formatTimestampToJST = (timestamp: any): string => {
    try {
      let date: Date
      
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        // Firebase Timestamp object (already in UTC)
        date = new Date(timestamp.seconds * 1000)
      } else if (timestamp instanceof Date) {
        // JavaScript Date object
        date = timestamp
      } else if (typeof timestamp === 'number') {
        // Unix timestamp
        date = new Date(timestamp)
      } else {
        // Fallback to current time if timestamp format is unknown
        date = new Date()
      }
      
      // Convert UTC to JST using toLocaleString with Asia/Tokyo timezone
      const jstString = date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
      
      // Convert from Japanese format (YYYY/MM/DD HH:mm:ss) to desired format (YYYY-MM-DD HH:mm:ss)
      return jstString.replace(/\//g, '-')
      
    } catch (error) {
      console.error('Error formatting timestamp:', error)
      // Fallback to current JST time
      return new Date().toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    }
  }

  const handleExportCSV = async () => {
    if (!db) {
      showMessage('データベースに接続されていません', 'error')
      return
    }

    setIsLoading(true)
    try {
      showMessage('データを取得しています...', 'info')
      
      // Adminダッシュボードと同じデータを取得
      const stats = await analytics.getSessionStats(-1)
      
      if (!stats.events || stats.events.length === 0) {
        showMessage('エクスポートするデータがありません', 'error')
        return
      }

      // サマリーデータを作成
      const summaryData: SummaryData[] = [
        {
          metric: 'トップに訪れた数',
          total: stats.visitorsCount,
          ...getDeviceBreakdown(stats.events, 'session_start'),
          pc: getDeviceBreakdown(stats.events, 'session_start').desktop,
          mobilePercent: '',
          pcPercent: ''
        },
        {
          metric: '再生した人の数',
          total: stats.playersCount,
          ...getDeviceBreakdown(stats.events, 'audio_play_start'),
          pc: getDeviceBreakdown(stats.events, 'audio_play_start').desktop,
          mobilePercent: '',
          pcPercent: ''
        },
        {
          metric: '3分以上再生した人の数',
          total: stats.play3MinCount,
          ...getDeviceBreakdown(stats.events, 'audio_milestone_3min'),
          pc: getDeviceBreakdown(stats.events, 'audio_milestone_3min').desktop,
          mobilePercent: '',
          pcPercent: ''
        },
        {
          metric: '最後まで再生した人の数',
          total: stats.completersCount,
          ...getDeviceBreakdown(stats.events, 'completion_reached'),
          pc: getDeviceBreakdown(stats.events, 'completion_reached').desktop,
          mobilePercent: '',
          pcPercent: ''
        },
        {
          metric: 'アンケートに答えた人の数',
          total: stats.surveyRespondentsCount,
          ...getDeviceBreakdown(stats.events, 'survey_completed'),
          pc: getDeviceBreakdown(stats.events, 'survey_completed').desktop,
          mobilePercent: '',
          pcPercent: ''
        }
      ]

      // パーセンテージを計算
      summaryData.forEach(item => {
        const total = item.mobile + item.desktop
        item.mobilePercent = total > 0 ? ((item.mobile / total) * 100).toFixed(1) + '%' : '0.0%'
        item.pcPercent = total > 0 ? ((item.desktop / total) * 100).toFixed(1) + '%' : '0.0%'
      })

      // アンケート結果データを作成
      const surveyEvents = stats.events.filter(e => e.eventType === 'survey_completed')
      const surveyData: SurveyData[] = [
        { rating: 'Excellent', count: 0, percentage: '0.0%' },
        { rating: 'Good', count: 0, percentage: '0.0%' },
        { rating: 'Fair', count: 0, percentage: '0.0%' },
        { rating: 'Poor', count: 0, percentage: '0.0%' }
      ]

      surveyData[0].count = surveyEvents.filter(e => e.surveyRating === 'excellent').length
      surveyData[1].count = surveyEvents.filter(e => e.surveyRating === 'good').length
      surveyData[2].count = surveyEvents.filter(e => e.surveyRating === 'fair').length
      surveyData[3].count = surveyEvents.filter(e => e.surveyRating === 'poor').length

      const totalSurveys = surveyData.reduce((sum, item) => sum + item.count, 0)
      surveyData.forEach(item => {
        item.percentage = totalSurveys > 0 ? ((item.count / totalSurveys) * 100).toFixed(1) + '%' : '0.0%'
      })

      // CSVコンテンツ作成
      const csvContent = [
        '# HEP-FIVE Audio Guide Analytics Summary',
        `# Export Date: ${new Date().toISOString().split('T')[0]}`,
        '',
        '# Main Metrics',
        'メトリック,合計,モバイル,PC,モバイル割合,PC割合',
        ...summaryData.map(row => 
          `"${row.metric}",${row.total},${row.mobile},${row.desktop},${row.mobilePercent},${row.pcPercent}`
        ),
        '',
        '# Survey Results',
        'Rating,Count,Percentage',
        ...surveyData.map(row => 
          `${row.rating},${row.count},${row.percentage}`
        ),
        '',
        `# Total Survey Responses: ${totalSurveys}`
      ].join('\n')

      // ファイルダウンロード
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hep_five_summary_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showMessage(`サマリーデータをCSVでエクスポートしました (${summaryData.length + surveyData.length}項目)`, 'success')
      
    } catch (error) {
      console.error('CSV export failed:', error)
      showMessage('CSVエクスポートに失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportDetailedLogs = async () => {
    if (!db) {
      showMessage('データベースに接続されていません', 'error')
      return
    }

    setIsLoading(true)
    try {
      showMessage('詳細ログを取得しています...', 'info')
      
      // 新しい detailed_logs コレクションから直接データを取得
      const q = query(collection(db, 'detailed_logs'))
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        showMessage('エクスポートする詳細ログがありません', 'error')
        return
      }

      const detailedLogs: DetailedEventLog[] = []
      
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        detailedLogs.push({
          timestamp: formatTimestampToJST(data.timestamp),
          event: data.eventType,
          device: data.deviceType,
          additionalInfo: data.additionalInfo || ''
        })
      })

      // タイムスタンプでソート
      detailedLogs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

      // CSVコンテンツ作成
      const csvContent = [
        'Timestamp,Event,Device,Additional Info',
        ...detailedLogs.map(log => 
          `${log.timestamp},${log.event},${log.device},${log.additionalInfo}`
        )
      ].join('\n')

      // ファイルダウンロード
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hep_five_detailed_logs_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showMessage(`詳細ログをCSVでエクスポートしました (${detailedLogs.length}件)`, 'success')
      
    } catch (error) {
      console.error('Detailed logs export failed:', error)
      showMessage('詳細ログエクスポートに失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetDatabase = async () => {
    if (!db) {
      showMessage('データベースに接続されていません', 'error')
      return
    }

    const confirmed = window.confirm(
      '⚠️ 警告: この操作により全てのアナリティクスデータが削除されます。\n' +
      'この操作は元に戻すことができません。\n\n' +
      '続行しますか？'
    )
    
    if (!confirmed) return

    const doubleConfirmed = window.confirm(
      '最終確認: 本当に全てのデータを削除しますか？\n' +
      '「OK」をクリックするとデータが完全に削除されます。'
    )
    
    if (!doubleConfirmed) return

    setIsLoading(true)
    try {
      showMessage('データベースをリセットしています...', 'info')
      
      const q = query(collection(db, 'analytics_events'))
      const snapshot = await getDocs(q)
      
      const deletePromises: Promise<void>[] = []
      snapshot.docs.forEach(document => {
        deletePromises.push(deleteDoc(doc(db, 'analytics_events', document.id)))
      })

      // セッションサマリーも削除
      try {
        const summaryQuery = query(collection(db, 'session_summaries'))
        const summarySnapshot = await getDocs(summaryQuery)
        summarySnapshot.docs.forEach(document => {
          deletePromises.push(deleteDoc(doc(db, 'session_summaries', document.id)))
        })
      } catch (error) {
        console.warn('Session summaries collection not found or error:', error)
      }

      // 新しい detailed_logs コレクションも削除
      try {
        const detailedLogsQuery = query(collection(db, 'detailed_logs'))
        const detailedLogsSnapshot = await getDocs(detailedLogsQuery)
        detailedLogsSnapshot.docs.forEach(document => {
          deletePromises.push(deleteDoc(doc(db, 'detailed_logs', document.id)))
        })
      } catch (error) {
        console.warn('Detailed logs collection not found or error:', error)
      }

      await Promise.all(deletePromises)
      
      // ローカルストレージもクリア
      localStorage.clear()
      sessionStorage.clear()
      
      showMessage(`${snapshot.docs.length}件のデータを削除しました`, 'success')
      
    } catch (error) {
      console.error('Database reset failed:', error)
      showMessage('データベースリセットに失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoBack = () => {
    window.history.back()
  }

  if (!isAuthenticated) {
    return (
      <PasswordAuth
        onSuccess={() => setIsAuthenticated(true)}
        pageName="データベース"
      />
    )
  }

  return (
    <div className="database-management">
      <div className="management-container">
        <div className="management-header">
          <h1>🗄️ データベース管理</h1>
          <button 
            className="back-button"
            onClick={handleGoBack}
          >
            ← 戻る
          </button>
        </div>

        {message && (
          <div className={`message ${messageType}`}>
            {message}
          </div>
        )}

        <div className="management-actions">
          <div className="action-card">
            <div className="action-icon">📊</div>
            <div className="action-content">
              <h2>サマリーデータエクスポート</h2>
              <p>集計されたアナリティクスサマリーをCSVファイルでダウンロードします。</p>
              <button 
                className="action-button export-button"
                onClick={handleExportCSV}
                disabled={isLoading}
              >
                {isLoading ? 'エクスポート中...' : 'サマリーをダウンロード'}
              </button>
            </div>
          </div>

          <div className="action-card">
            <div className="action-icon">📋</div>
            <div className="action-content">
              <h2>詳細ログエクスポート</h2>
              <p>個々のイベントログを時系列でCSVファイルでダウンロードします。</p>
              <button 
                className="action-button export-button"
                onClick={handleExportDetailedLogs}
                disabled={isLoading}
              >
                {isLoading ? 'エクスポート中...' : '詳細ログをダウンロード'}
              </button>
            </div>
          </div>

          <div className="action-card danger">
            <div className="action-icon">⚠️</div>
            <div className="action-content">
              <h2>データベースリセット</h2>
              <p>
                全てのアナリティクスデータを削除します。<br />
                <strong>この操作は元に戻すことができません。</strong>
              </p>
              <button 
                className="action-button reset-button"
                onClick={handleResetDatabase}
                disabled={isLoading}
              >
                {isLoading ? 'リセット中...' : '全データ削除'}
              </button>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>ℹ️ 注意事項</h3>
          <ul>
            <li>サマリーデータ: 集計された統計情報（デバイス別の訪問者数、完了率など）</li>
            <li>詳細ログ: 個々のイベント（Visit, Play, Play3min, Completion, Survey）を時系列で出力</li>
            <li>CSVエクスポートには時間がかかる場合があります</li>
            <li>データリセットは完全に元に戻すことができません</li>
            <li>重要なデータは事前にエクスポートしてバックアップを取ってください</li>
            <li>この操作はFirestoreエミュレーター使用時にのみ実行してください</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default DatabaseManagement